/**
 * Capabilities, client side.
 *
 * Deliberately *not* a copy of the server's role→permission table. The browser
 * asks `GET /api/auth/capabilities` and renders from the answer, because a
 * second copy of that table would drift the first time somebody edited one —
 * and the failure mode is invisible: a button that renders and then 403s, or one
 * that stays hidden from somebody entitled to it.
 *
 * What lives here is the part a component actually needs: the permission
 * *names*, so a screen can say which capability it requires, and the Arabic
 * labels the hospital admin reads when ticking privileges for a co-admin.
 * `Tests/suites/frontend/rbac-parity.test.mjs` asserts this name list is
 * identical to `backend/lib/rbac.js` — that is the only thing that can drift,
 * and it is one assertion.
 *
 * This mirrors the arrangement `domain/pricing.js` has with `lib/pricing.js`,
 * with one difference worth stating: pricing is duplicated because the browser
 * must *quote* a price before the server stores one. Nothing here is computed
 * before the server has answered, so nothing here needs duplicating.
 */

/** Every capability name the platform recognises. Must match lib/rbac.js. */
export const PERMISSIONS = Object.freeze({
  PLATFORM_ADMIN: 'PLATFORM_ADMIN',

  HOSPITAL_SETTINGS_MANAGE: 'HOSPITAL_SETTINGS_MANAGE',
  HOSPITAL_STAFF_MANAGE: 'HOSPITAL_STAFF_MANAGE',
  HOSPITAL_STAFF_VIEW: 'HOSPITAL_STAFF_VIEW',
  HOSPITAL_FINANCE_VIEW: 'HOSPITAL_FINANCE_VIEW',
  HOSPITAL_SERVICES_MANAGE: 'HOSPITAL_SERVICES_MANAGE',

  APPOINTMENT_VIEW: 'APPOINTMENT_VIEW',
  APPOINTMENT_BOOK: 'APPOINTMENT_BOOK',
  APPOINTMENT_CANCEL: 'APPOINTMENT_CANCEL',
  INTAKE_FIELDS_MANAGE: 'INTAKE_FIELDS_MANAGE',
  SHARE_LINK_ISSUE: 'SHARE_LINK_ISSUE',
  SHARE_LINK_REVOKE: 'SHARE_LINK_REVOKE',
  PAYMENT_SUBMIT: 'PAYMENT_SUBMIT',

  CLINIC_MANAGE: 'CLINIC_MANAGE',
  DOCTOR_ASSIGN: 'DOCTOR_ASSIGN',
  SCHEDULE_CONFIGURE: 'SCHEDULE_CONFIGURE',
  SCHEDULE_SHIFT: 'SCHEDULE_SHIFT',
  QUEUE_OVERSEE: 'QUEUE_OVERSEE',
  PAYMENT_CONFIRM: 'PAYMENT_CONFIRM',

  QUEUE_ADVANCE: 'QUEUE_ADVANCE',
  PATIENT_RECORD_VIEW: 'PATIENT_RECORD_VIEW',
  PATIENT_RECORD_APPEND: 'PATIENT_RECORD_APPEND',
  PRESCRIPTION_WRITE: 'PRESCRIPTION_WRITE',
  CONSULTATION_FINISH: 'CONSULTATION_FINISH',

  PERSONAL_CLINIC_MANAGE: 'PERSONAL_CLINIC_MANAGE',

  AI_ANALYST: 'AI_ANALYST',
  AI_PREMIUM_MODEL: 'AI_PREMIUM_MODEL',
});

export const ALL_PERMISSIONS = Object.freeze(Object.values(PERMISSIONS));

/**
 * What each capability is called on the privileges screen.
 *
 * Arabic, because a hospital admin ticking boxes for their deputy is the only
 * person who ever reads these. The English one-liners on the `Permissions` rows
 * are for an operator reading the database.
 */
export const PERMISSION_LABELS = Object.freeze({
  [PERMISSIONS.PLATFORM_ADMIN]: 'إدارة المنصة بالكامل',

  [PERMISSIONS.HOSPITAL_SETTINGS_MANAGE]: 'تعديل بيانات المنشأة وإعداداتها',
  [PERMISSIONS.HOSPITAL_STAFF_MANAGE]: 'إنشاء حسابات الموظفين وإيقافها',
  [PERMISSIONS.HOSPITAL_STAFF_VIEW]: 'الاطلاع على قائمة الموظفين دون تعديلها',
  [PERMISSIONS.HOSPITAL_FINANCE_VIEW]: 'الاطلاع على الحسابات والإيرادات',
  [PERMISSIONS.HOSPITAL_SERVICES_MANAGE]: 'إدارة الخدمات والأسعار',

  [PERMISSIONS.APPOINTMENT_VIEW]: 'عرض حجوزات أطباء المنشأة',
  [PERMISSIONS.APPOINTMENT_BOOK]: 'حجز موعد نيابة عن المريض',
  [PERMISSIONS.APPOINTMENT_CANCEL]: 'إلغاء الحجوزات',
  [PERMISSIONS.INTAKE_FIELDS_MANAGE]: 'إنشاء حقول استقبال مخصصة',
  [PERMISSIONS.SHARE_LINK_ISSUE]: 'إصدار رابط مشاركة للمريض',
  [PERMISSIONS.SHARE_LINK_REVOKE]: 'إلغاء رابط مشاركة',
  [PERMISSIONS.PAYMENT_SUBMIT]: 'تسجيل استلام مبلغ من المريض',

  [PERMISSIONS.CLINIC_MANAGE]: 'إدارة العيادات والغرف',
  [PERMISSIONS.DOCTOR_ASSIGN]: 'توزيع الأطباء على العيادات',
  [PERMISSIONS.SCHEDULE_CONFIGURE]: 'ضبط المواعيد والغرف المتوازية',
  [PERMISSIONS.SCHEDULE_SHIFT]: 'تأجيل مواعيد اليوم وإخطار المرضى',
  [PERMISSIONS.QUEUE_OVERSEE]: 'الإشراف على جميع طوابير الانتظار',
  [PERMISSIONS.PAYMENT_CONFIRM]: 'تأكيد تحصيل المدفوعات',

  [PERMISSIONS.QUEUE_ADVANCE]: 'إدارة طابور طبيب واحد',
  [PERMISSIONS.PATIENT_RECORD_VIEW]: 'الاطلاع على الملف الطبي',
  [PERMISSIONS.PATIENT_RECORD_APPEND]: 'الإضافة إلى الملف الطبي',
  [PERMISSIONS.PRESCRIPTION_WRITE]: 'كتابة وتعديل الروشتات',
  [PERMISSIONS.CONSULTATION_FINISH]: 'إنهاء الكشف وإخطار السكرتارية',

  [PERMISSIONS.PERSONAL_CLINIC_MANAGE]: 'إدارة العيادة الخاصة (عروض، أكواد، باقات)',

  [PERMISSIONS.AI_ANALYST]: 'تحليل الملف الطبي بالذكاء الاصطناعي',
  [PERMISSIONS.AI_PREMIUM_MODEL]: 'استخدام النموذج المدفوع المتقدم',
});

/**
 * The privileges a hospital admin may hand to a co-admin.
 *
 * Mirrors CO_ADMIN_GRANTABLE on the server, which filters the same two names
 * out of any stored bundle. Listed here so the ticking screen does not offer a
 * box that the server would silently drop — a checkbox that unticks itself on
 * save is worse than one that was never there.
 */
export const GRANTABLE_PERMISSIONS = Object.freeze(
  ALL_PERMISSIONS.filter(
    (name) => name !== PERMISSIONS.PLATFORM_ADMIN && name !== PERMISSIONS.AI_PREMIUM_MODEL,
  ),
);

/** How the privileges screen groups the checkboxes. Presentation only. */
export const PERMISSION_GROUPS = Object.freeze([
  {
    label: 'إدارة المنشأة',
    permissions: [
      PERMISSIONS.HOSPITAL_SETTINGS_MANAGE,
      PERMISSIONS.HOSPITAL_STAFF_MANAGE,
      PERMISSIONS.HOSPITAL_STAFF_VIEW,
      PERMISSIONS.HOSPITAL_FINANCE_VIEW,
      PERMISSIONS.HOSPITAL_SERVICES_MANAGE,
    ],
  },
  {
    label: 'الاستقبال والحجوزات',
    permissions: [
      PERMISSIONS.APPOINTMENT_VIEW,
      PERMISSIONS.APPOINTMENT_BOOK,
      PERMISSIONS.APPOINTMENT_CANCEL,
      PERMISSIONS.INTAKE_FIELDS_MANAGE,
      PERMISSIONS.SHARE_LINK_ISSUE,
      PERMISSIONS.SHARE_LINK_REVOKE,
      PERMISSIONS.PAYMENT_SUBMIT,
    ],
  },
  {
    label: 'تشغيل العيادات',
    permissions: [
      PERMISSIONS.CLINIC_MANAGE,
      PERMISSIONS.DOCTOR_ASSIGN,
      PERMISSIONS.SCHEDULE_CONFIGURE,
      PERMISSIONS.SCHEDULE_SHIFT,
      PERMISSIONS.QUEUE_OVERSEE,
      PERMISSIONS.PAYMENT_CONFIRM,
    ],
  },
  {
    label: 'غرفة الكشف',
    permissions: [
      PERMISSIONS.QUEUE_ADVANCE,
      PERMISSIONS.PATIENT_RECORD_VIEW,
      PERMISSIONS.PATIENT_RECORD_APPEND,
      PERMISSIONS.PRESCRIPTION_WRITE,
      PERMISSIONS.CONSULTATION_FINISH,
      PERMISSIONS.AI_ANALYST,
    ],
  },
  {
    label: 'العيادة الخاصة',
    permissions: [PERMISSIONS.PERSONAL_CLINIC_MANAGE],
  },
]);

/** The Arabic name of each hospital staff account type. */
export const MEMBER_ROLE_LABELS = Object.freeze({
  manager: 'مدير المنشأة',
  co_admin: 'مدير مساعد',
  receptionist: 'موظف استقبال',
  operations_manager: 'مدير العيادات والتشغيل',
  secretary: 'سكرتير طبيب',
  doctor: 'طبيب',
  staff: 'موظف',
});

/** The account types a hospital admin may create. Mirrors CREATABLE_MEMBER_ROLES. */
export const CREATABLE_MEMBER_ROLES = Object.freeze([
  'co_admin',
  'receptionist',
  'operations_manager',
  'secretary',
  'doctor',
  'staff',
]);
